<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const titles = ['QA Engineer', 'Frontend Developer', 'Photographer', 'Problem Solver', 'Curious']
const titleIndex = ref(0)
const imageVisible = ref(false)
const contentVisible = ref(false)
let interval = null

onMounted(() => {
  interval = setInterval(() => {
    titleIndex.value = (titleIndex.value + 1) % titles.length
  }, 2000)

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return
      if (target.classList.contains('about-image')) imageVisible.value = true
      if (target.classList.contains('about-content')) contentVisible.value = true
      observer.unobserve(target)
    })
  })

  document.querySelectorAll('.about-image, .about-content')
    .forEach(el => observer.observe(el))
})

onUnmounted(() => clearInterval(interval))
</script>


<template>
  <section class="about-section">
    <div class="blob" />

    <div :class="['about-image', { visible: imageVisible }]">
      <img src="@/assets/naelia.png" alt="Naelia Salas" />
    </div>

    <div :class="['about-content', { visible: contentVisible }]">
      <span class="label">About</span>
      <h2 class="animated-header">{{ titles[titleIndex] }}</h2>

      <div class="about-text">
        <p>Frontend developer turned QA Engineer, with a background in photography and customer care that shaped an eye for detail and a high bar for quality.</p>
        <p>I've spent the last two years moving from building UIs to owning what ships — working across manual testing, triage, and CI pipelines. Understanding code from the developer side means I catch issues earlier and communicate them more precisely.</p>
        <p>Currently contributing to a globally scaled AI voice assistant with millions of users — evaluating dialogue quality, investigating high-risk scenarios, and improving cross-language user experiences.</p>
        <p>
          Find me on
          <a href="https://www.linkedin.com/in/naeliasalas/" target="_blank">LinkedIn</a> or
          <a href="https://github.com/naeliasalasamner" target="_blank">GitHub</a>.
        </p>
      </div>

      <a download="NaeliaSalasCv.pdf" href="/NaeliaSalasCv.pdf">
        <button class="cv-button">Download CV</button>
      </a>
    </div>
  </section>
</template>

<style scoped>
.about-section {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  max-width: 1600px;
  margin: 0 auto;
  padding: 4rem 2rem;
  gap: 5rem;
}

/* Blob background */
.blob {
  position: absolute;
  top: 50%;
  left: -10%;
  width: 50%;
  height: 100%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.25), transparent 70%);
  transform: translateY(-50%);
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}

/* Image */
.about-image {
  flex-shrink: 0;
  opacity: 0;
  transform: translateX(-60px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.about-image.visible {
  opacity: 1;
  transform: translateX(0);
}

.about-image img {
  width: 320px;
  height: 420px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Content */
.about-content {
  flex: 1;
  max-width: 560px;
  opacity: 0;
  transform: translateX(60px);
  transition: opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s;
}

.about-content.visible {
  opacity: 1;
  transform: translateX(0);
}

.label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: #667eea;
  font-weight: 600;
}

.animated-header {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin: 0.5rem 0 1.5rem;
  letter-spacing: -1px;
  min-height: 2.5rem;
}

.about-text p {
  font-size: 1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 1rem;
}

.about-text a {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
}

.about-text a:hover { text-decoration: underline }

.cv-button {
  margin-top: 2rem;
  padding: 0.9rem 2.2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  transition: all 0.3s;
}

.cv-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(102, 126, 234, 0.4);
}

@media (max-width: 768px) {
  .about-section { flex-direction: column; align-items: center; gap: 2rem }
  .about-image { opacity: 1; transform: none }
  .about-image img { width: 100%; max-width: 280px; height: 360px }
  .about-content { opacity: 1; transform: none; max-width: 100%; text-align: center }
  .about-text p { text-align: center }
}
</style>